import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import 'bootstrap/dist/css/bootstrap.min.css';
import { Analytics } from '@vercel/analytics/react';
import { initAnalytics } from './services/analytics';
import MapView from './components/MapView';
import CaseStudyDetail from './components/CaseStudyDetail';
import AdminDashboard from './components/AdminDashboard';
import PlatformAdminDashboard from './components/PlatformAdminDashboard';
import CaseStudyForm from './components/CaseStudyForm';
import Login from './components/Login';
import OAuthCallback from './components/OAuthCallback';
import Navigation from './components/Navigation';
import EmbeddedMapView from './components/EmbeddedMapView';
import LandingPage from './components/landing/LandingPage';
import SolarLandingPage from './components/landing/SolarLandingPage';
import TradesLandingPage from './components/landing/TradesLandingPage';
import SolarMapPage from './components/landing/SolarMapPage';
import TermsOfService from './components/landing/TermsOfService';
import PrivacyPolicy from './components/landing/PrivacyPolicy';
import ImpersonationBanner from './components/ImpersonationBanner';
import { AuthProvider } from './contexts/AuthContext';
import PrivateRoute from './components/PrivateRoute';

// Module-load init: PostHog reads REACT_APP_POSTHOG_KEY and no-ops if absent.
initAnalytics();

const AppContent: React.FC = () => {
  const location = useLocation();
  const isEmbedRoute = location.pathname.includes('/embed');
  const isLandingPage = location.pathname === '/';
  // Vertical landing + public demo maps render their own chrome (LandingNav / map strip)
  // and must not get the app-level Navigation or impersonation banner painted on top.
  const isVerticalLanding = location.pathname.startsWith('/for/');
  const isPublicDemoMap = location.pathname.startsWith('/maps/');
  const isLegalPage = location.pathname === '/terms' || location.pathname === '/privacy';
  const hideAppChrome = isEmbedRoute || isLandingPage || isVerticalLanding || isPublicDemoMap || isLegalPage;

  return (
    <div className="App">
      {!hideAppChrome && <ImpersonationBanner />}
      {!hideAppChrome && <Navigation />}
      <Routes>
        <Route path="/" element={<LandingPage />} />
        <Route path="/for/solar" element={<SolarLandingPage />} />
        <Route path="/for/trades" element={<TradesLandingPage />} />
        <Route path="/maps/solar-uk" element={<SolarMapPage />} />
        <Route path="/terms" element={<TermsOfService />} />
        <Route path="/privacy" element={<PrivacyPolicy />} />
        <Route path="/embed" element={<EmbeddedMapView />} />
        <Route path="/embed/:clientId" element={<EmbeddedMapView />} />

        {/* New user-scoped routes */}
        <Route path="/:userId" element={<MapView />} />
        <Route path="/:userId/case-study/:projectId" element={<CaseStudyDetail />} />

        {/* Legacy routes for backwards compatibility */}
        <Route path="/case-study/:id" element={<CaseStudyDetail />} />

        <Route path="/oauth/callback" element={<OAuthCallback />} />
        <Route path="/login" element={<Login />} />
        <Route path="/:userId/login" element={<Login />} />
        <Route path="/admin" element={
          <PrivateRoute>
            <AdminDashboard />
          </PrivateRoute>
        } />
        {/* Must be registered explicitly — otherwise it falls through to the "*" catch-all
            below and silently redirects to the landing page. */}
        <Route path="/admin/platform" element={
          <PrivateRoute requireAdmin>
            <PlatformAdminDashboard />
          </PrivateRoute>
        } />
        <Route path="/admin/case-study/new" element={
          <PrivateRoute>
            <CaseStudyForm />
          </PrivateRoute>
        } />
        <Route path="/admin/case-study/edit/:id" element={
          <PrivateRoute>
            <CaseStudyForm />
          </PrivateRoute>
        } />

        {/* User-scoped admin routes */}
        <Route path="/:userId/admin" element={
          <PrivateRoute>
            <AdminDashboard />
          </PrivateRoute>
        } />
        <Route path="/:userId/admin/case-study/new" element={
          <PrivateRoute>
            <CaseStudyForm />
          </PrivateRoute>
        } />
        <Route path="/:userId/admin/case-study/edit/:projectId" element={
          <PrivateRoute>
            <CaseStudyForm />
          </PrivateRoute>
        } />

        <Route path="*" element={<Navigate to="/" />} />
      </Routes>
    </div>
  );
};

function App() {
  return (
    <AuthProvider>
      <Router>
        <AppContent />
      </Router>
      <Analytics />
    </AuthProvider>
  );
}

export default App;
